import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, Envelope, MapPin, InstagramLogo } from '@phosphor-icons/react';
import { useCookieConsent } from '../context/CookieConsentContext';

const Footer = () => {
  const { trackEvent, resetConsent } = useCookieConsent();

  const handlePhoneClick = () => {
    trackEvent('Contact', { contact_method: 'phone_footer' });
  };

  const scrollToForm = () => {
    const formSection = document.getElementById('lead-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-dark-tertiary border-t border-white/5" data-testid="footer">
      {/* CTA Section */}
      <div className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
              Bereit für Ihren Wohntraum?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-8">
              Entdecken Sie mit den Wohntraum-Experten die Möglichkeiten Ihrer Immobilie. 
              Kostenlose Erstberatung — unverbindlich und persönlich.
            </p>
            <button
              onClick={scrollToForm}
              className="bg-gold text-black font-medium uppercase tracking-wide text-sm px-8 py-4 hover:bg-gold-light transition-all"
              data-testid="footer-cta">

              Jetzt Beratung anfragen
            </button>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Brand */}
            <div>
              <h3 className="text-gold font-sans text-sm uppercase tracking-[0.2em] mb-2">
                Wohntraum Experten
              </h3>
              <p className="text-white/40 text-xs mb-4">by venta immobilien</p>
              <p className="text-white/60 text-sm leading-relaxed">
                Schlüsselfertige Komplettsanierung — modern, effizient, wertsteigernd. 
                Mit fast 20 Jahren Erfahrung.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-medium text-sm uppercase tracking-wide mb-4">
                Kontakt
              </h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="tel:+4921293758775"
                    onClick={handlePhoneClick}
                    className="flex items-center gap-3 text-white/60 hover:text-gold transition-colors text-sm"
                    data-testid="footer-phone-haan">

                    <Phone size={16} weight="light" />
                    02129/375877-5
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+491707441918"
                    onClick={handlePhoneClick}
                    className="flex items-center gap-3 text-white/60 hover:text-gold transition-colors text-sm"
                    data-testid="footer-phone-mobil">

                    <Phone size={16} weight="light" />
                    Mobil: +49 170 7441918
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@wohntraum-experten.de"
                    className="flex items-center gap-3 text-white/60 hover:text-gold transition-colors text-sm"
                    data-testid="footer-email">

                    <Envelope size={16} weight="light" />
                    info@wohntraum-experten.de
                  </a>
                </li>
              </ul>
            </div>

            {/* Address */}
            <div>
              <h4 className="text-white font-medium text-sm uppercase tracking-wide mb-4">
                Anschrift
              </h4>
              <div className="flex items-start gap-3 text-white/60 text-sm">
                <MapPin size={16} weight="light" className="mt-1 shrink-0" />
                <div>
                  <p>Wohntraum Experten by veNta Immobilien</p>
                  <p>Wilhelmstraße 3</p>
                  <p>42781 Haan</p>
                </div>
              </div>
              <a
                href="https://www.instagram.com/neises_realestate"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/60 hover:text-gold transition-colors text-sm mt-4"
                data-testid="footer-instagram">

                <InstagramLogo size={16} weight="light" />
                @wohntraum_experten
              </a>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-medium text-sm uppercase tracking-wide mb-4">
                Rechtliches
              </h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="http://511816786.swh.strato-hosting.eu/impressum/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/60 hover:text-gold transition-colors text-sm"
                    data-testid="footer-impressum">

                    Impressum
                  </a>
                </li>
                <li>
                  <a
                    href="http://511816786.swh.strato-hosting.eu/datenschutz/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/60 hover:text-gold transition-colors text-sm"
                    data-testid="footer-datenschutz">

                    Datenschutzerklärung
                  </a>
                </li>
                <li>
                  <button
                    onClick={resetConsent}
                    className="text-white/60 hover:text-gold transition-colors text-sm"
                    data-testid="footer-cookie-settings">

                    Cookie-Einstellungen
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-white/40 text-xs text-center">
            © {new Date().getFullYear()} veNta Grundbesitz GmbH. Alle Rechte vorbehalten.
          </p>
        </div>
      </div>
    </footer>);

};

export default Footer;